/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { IProductsDimensionsAvailabilities } from '@msdyn365-commerce/commerce-entities';
import { IPriceComponentResources } from '@msdyn365-commerce/components';
import * as Msdyn365 from '@msdyn365-commerce/core';
import { getCatalogId, getUrlSync, ICoreContext } from '@msdyn365-commerce/core';
import { ProductPrice, ProductSearchResult, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { ProductComponent } from './components/product.component';
import { getCartState } from '@msdyn365-commerce/global-state';

import ProductCollectionQty from './product-collection-qty';
import {
    ArrayExtensions,
    DimensionAvailabilitiesForProductSearchResultInput,
    DimensionTypes,
    DimensionSwatchDisplayTypes,
    getDimensionAvailabilitiesForProductSearchResultAction,
    getPriceForProductSearchResult,
    IDimensionsApp,
    ObjectExtensions,
    PriceForProductSearchResultInput,
    validateCatalogId
} from '@msdyn365-commerce-modules/retail-actions';
//import { ICartActionResult, getCartState } from '@msdyn365-commerce/global-state';
import {
    Button,
    getTelemetryObject,
    IComponentNodeProps,
    IModuleProps,
    INodeProps,
    ISingleSlideCarouselProps,
    ITelemetryContent,
    NodeTag,
    SingleSlideCarousel
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { IProductCollectionData } from './product-collection.data';
import { IProductCollectionProps, layout } from './product-collection.props.autogenerated';

export interface IProductCollectionViewProps extends IProductCollectionProps<IProductCollectionData> {
    heading?: React.ReactNode;
    ProductCollectionContainer: IModuleProps;
    SingleSlideCarouselComponentProps: INodeProps;
    GridComponentProps: INodeProps;
    products: IProductComponentViewProps[];
    isCarousel: boolean;
    productPrices?: ProductPrice[];
    seeAllButton?: React.ReactNode;
}

export interface IProductComponentViewProps {
    ProductContainer: INodeProps;
    productComponent: React.ReactNode;
}
export interface IProductComponentState {
    products: ProductSearchResult[];
    productsDimensionAvailabilities?: IProductsDimensionsAvailabilities[][];
    cartState: any;
    valueher: any;
    oosIds: [];
    activeProductsList: ProductSearchResult[];
}

/**
 * ProductCollection component.
 */
class ProductCollection extends React.PureComponent<IProductCollectionProps<IProductCollectionData>, IProductComponentState> {
    private readonly telemetryContent: ITelemetryContent;

    private readonly productPrices: ProductPrice[];

    constructor(props: IProductCollectionProps<IProductCollectionData>) {
        super(props);
        this.productPrices = [];
        this.state = {
            oosIds: [],
            products: this.props.config.productCollection.products || [],
            activeProductsList: [],
            cartState: '',
            valueher: '' || null
        };
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        void this._getCartStatusUpdate();
    }

    public async componentDidMount(): Promise<void> {
        const ids = await this.fetchOOSProductIds();
        this.setState({ oosIds: ids });
        // await this._getCartStatusUpdate();
        if (this.props.config.enableAffiliationBasedPricing && this.props.context.actionContext.requestContext.user.isAuthenticated) {
            await this._getActivePrice();
        }
    }

    public render(): JSX.Element | null {
        const { heading, className, shouldDisplaySeeAllButton } = this.props.config;

        const { resources } = this.props;
        const recommendation = this.props.config.productCollection.recommendationListId;

        // Initialize state in case if the products were not found during initial module render
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- Initialize data if not there.
        this.initProductState(this.props.config.productCollection);

        const products = this.state.activeProductsList;
        const isCarousel = this.props.config.layout === layout.carousel;
        const headingLabel = heading ? heading.text : '';
        const headingComponent = heading && (
            <Msdyn365.Text
                className='ms-product-collection__heading'
                tag={heading.tag || 'h1'}
                text={headingLabel}
                editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
            />
        );

        // @ts-ignore
        let collectionGridBg = '0xffFFFFFF'; // By Default Grid Background
        // @ts-ignore
        if (this.props.config.imageSettings.backgroundColor) {
            // @ts-ignore
            collectionGridBg = this.props.config.imageSettings.backgroundColor;
        }
        collectionGridBg = collectionGridBg.replace('0xff', '#');
        if (collectionGridBg !== '#fff') {
            collectionGridBg = `linear-gradient(180deg, ${collectionGridBg} 0%, #F9F8F9 100%)`;
        }
        const productCollectionViewProps = {
            ...this.props,
            heading: headingComponent,
            SingleSlideCarouselComponentProps: {
                tag: SingleSlideCarousel,
                className: '',
                flipperPrevLabel: `${headingLabel} ${resources.flipperPrevious}`,
                flipperNextLabel: `${headingLabel} ${resources.flipperNext}`,
                parentId: this.props.id,
                telemetryContent: this.telemetryContent
            } as IComponentNodeProps<ISingleSlideCarouselProps>,
            ProductCollectionContainer: {
                moduleProps: this.props,
                style: { background: collectionGridBg },
                className: classnames('ms-product-collection', className)
            },
            GridComponentProps: {
                tag: 'ul' as NodeTag,
                className: 'ms-product-collection__items'
            },
            isCarousel,

            products:
                ArrayExtensions.hasElements(products) &&
                products.filter(item => {
                    return item !== undefined;
                }),
            productPrices: this.productPrices,
            seeAllButton:
                shouldDisplaySeeAllButton && ArrayExtensions.hasElements(products)
                    ? this._getSeeAllButton(recommendation, headingLabel)
                    : undefined
        };
        return this.props.renderView(productCollectionViewProps) as React.ReactElement;
    }

    public async fetchActiveProductsList() {
        const { className } = this.props.config;
        const products = this.state.products;
        const OOSProductIds = this.state.oosIds;
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- product check for safety.
        const updatedProducts = products?.map((item: ProductSearchResult, index: number) => {
            let isOOS = false;
            if (OOSProductIds && OOSProductIds?.length > 0 && item?.RecordId) {
                const isExist = OOSProductIds.filter((id: number) => {
                    return id === item?.RecordId;
                });
                isOOS = isExist && isExist?.length > 0;
            }

            const getProductData = this._getProduct(item, index, className);
            return !isOOS && !ObjectExtensions.isNullOrUndefined(getProductData) ? getProductData : undefined;
            // }
        });
        if (updatedProducts && ObjectExtensions.isNullOrUndefined(updatedProducts)) {
            this.setState({ activeProductsList: updatedProducts });
        }
    }

    public async fetchOOSProductIds() {
        let response = [];
        const itemStr = localStorage.getItem('OOSData');

        if (!itemStr) {
            response = await this.getOOSProductData();
        } else {
            const item = JSON.parse(itemStr);
            const now = Date.now();
            response = item.value;
            // Check if the data is expired
            if (now > item.expiry) {
                localStorage.removeItem('OOSData'); // Remove expired data from local storage
                response = await this.getOOSProductData();
            }
        }
        return response;
    }

    public async getOOSProductData() {
        const minutes = 15;
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        const axios = require('axios');

        const config = {
            method: 'get',
            maxBodyLength: Infinity,
            url: 'https://api.localy.ae/fetchOOSData',
            headers: {}
        };

        return axios
            .request(config)
            .then((response: any) => {
                if (response?.data?.unavailableProductIds && response?.data?.unavailableProductIds?.length > 0) {
                    const expiryTimestamp = Date.now() + minutes * 60 * 1000; // Set expiry time in milliseconds
                    const item = {
                        value: response?.data?.unavailableProductIds,
                        expiry: expiryTimestamp
                    };
                    localStorage.setItem('OOSData', JSON.stringify(item));
                    return response?.data?.unavailableProductIds;
                }
            })
            .catch((error: any) => {
                console.log(error);
            });
    }

    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent) => (this.props.config.heading!.text = event.target.value);

    public _getCartStatusUpdate = async () => {
        const cartState = await getCartState(this.props.context.actionContext);
        console.log('the changes');
        console.log(cartState.cart);
        const cartLinesData = cartState.cart.CartLines;
        this.setState({ cartState: cartLinesData });
    };
    /**
     * Get products dimension availabilities.
     * @param products - Products.
     * @returns Promise.
     */
    private readonly _getProductsDimensionsAvailabilities = async (products: ProductSearchResult[]): Promise<void> => {
        const context = this.props.context as ICoreContext<IDimensionsApp>;
        const dimensionsToDisplayOnProductCard = context.app.config.dimensionsInProductCard;
        const dimensionsToDisplayAsSwatch = context.app.config.dimensionsAsSwatchType;
        const validProducts = ArrayExtensions.validValues(products);
        if (
            ArrayExtensions.hasElements(validProducts) &&
            ArrayExtensions.hasElements(dimensionsToDisplayOnProductCard) &&
            !dimensionsToDisplayOnProductCard.includes(DimensionTypes.none) &&
            ArrayExtensions.hasElements(dimensionsToDisplayAsSwatch) &&
            !dimensionsToDisplayAsSwatch.includes(DimensionTypes.none)
        ) {
            const productsDimensionAvailabilities = await getDimensionAvailabilitiesForProductSearchResultAction(
                new DimensionAvailabilitiesForProductSearchResultInput(
                    validProducts,
                    this.props.context as ICoreContext<IDimensionsApp>,
                    DimensionSwatchDisplayTypes.productCard
                ),
                this.props.context.actionContext
            );

            if (ArrayExtensions.hasElements(productsDimensionAvailabilities)) {
                this.setState({ productsDimensionAvailabilities });
            }
        }
    };

    /**
     * Helper method to initialize products state.
     * @param productCollection - List of products.
     */
    private readonly initProductState = async (productCollection: Msdyn365.IProductList) => {
        let products = this.state.products;
        const productCollectionProducts = productCollection.products;
        if (!ArrayExtensions.hasElements(products) && ArrayExtensions.hasElements(productCollectionProducts)) {
            products = productCollectionProducts;
            this.setState({ products });
        } else if (
            ArrayExtensions.hasElements(products) &&
            ArrayExtensions.hasElements(productCollectionProducts) &&
            !ArrayExtensions.equals(products, productCollectionProducts)
        ) {
            products = productCollectionProducts;
            this.setState({ products });
        } else if (productCollectionProducts === undefined && ArrayExtensions.hasElements(this.state.products)) {
            products = [];
            this.setState({ products: products });
        }

        if (Msdyn365.msdyn365Commerce.isBrowser && !ArrayExtensions.hasElements(this.state.productsDimensionAvailabilities)) {
            await this._getProductsDimensionsAvailabilities(products);
        }
    };

    private readonly _getProduct = (product: ProductSearchResult, index: number, className?: string): IProductComponentViewProps | null => {
        let isBundleCollection = false;
        if (className && className === 'BundleCollectionClass') {
            isBundleCollection = true;
        }
        if (!product || !product.Name) {
            this.props.telemetry.trace('[product-collection] Product or product name is null');
            return null;
        }
        const telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        const {
            imageSettings
            // , allowBackNavigation
        } = this.props.config;
        const resources = this.props.resources;
        // const breaCrumbType = this.props.context.app.config.breadcrumbType;
        // const allowBack = allowBackNavigation && (breaCrumbType === 'back' || breaCrumbType === 'categoryAndBack');
        const quickviewSlot =
            this.props.slots && this.props.slots.quickview && this.props.slots.quickview.length > 0 ? this.props.slots.quickview[0] : null;
        const productComparisonButtonSlot = ArrayExtensions.hasElements(this.props.slots.productComparisonButton)
            ? this.props.slots.productComparisonButton[0]
            : null;

        const reviewCount = 1;
        const reviewTotalRatings = Number(product.TotalRatings);
        const reviewCountAriaLabelValue =
            reviewTotalRatings > reviewCount ? resources.ratingCountAriaLabel : resources.ratingCountAriaLabelSingleUser;
        const isPriceMinMaxEnabled = this.props.data.featureState.result?.find(
            featureState => featureState.Name === 'Dynamics.AX.Application.RetailSearchPriceRangeFeature'
        );
        const priceResources: IPriceComponentResources = {
            priceRangeSeparator: resources.priceRangeSeparator
        };
        // console.log(this.state.cartState);
        // console.log(product);
        // if (this.state.cartState.length === 0) {
        const product1: SimpleProduct = {
            RecordId: product?.RecordId,
            ItemId: product?.ItemId,
            Name: product?.Name,
            Description: product?.Description,
            ProductTypeValue: product?.RecordId,
            DefaultUnitOfMeasure: product?.DefaultUnitOfMeasure,
            BasePrice: product?.BasePrice!,
            Price: product?.Price,
            AdjustedPrice: product?.Price,
            MasterProductId: product?.MasterProductId,
            PrimaryImageUrl: product?.PrimaryImageUrl,
            ExtensionProperties: product?.ExtensionProperties
        };
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        getCartState(this.props.context.actionContext).then(result => {
            this.setState({ valueher: 'value' });
            return result;
        });

        if (this.state.valueher === 'value') {
            return {
                ProductContainer: {
                    tag: 'li' as NodeTag,
                    className: 'ms-product-collection__item',
                    role: 'listitem',
                    key: index
                },
                productComponent: (
                    <>
                        <ProductComponent
                            context={this.props.context}
                            telemetryContent={telemetryContent}
                            imageSettings={imageSettings}
                            freePriceText={resources.priceFree}
                            originalPriceText={resources.originalPriceText}
                            currentPriceText={resources.currentPriceText}
                            ratingAriaLabel={resources.ratingAriaLabel}
                            ratingCountAriaLabel={reviewCountAriaLabelValue}
                            allowBack={false}
                            id={this.props.id}
                            typeName={this.props.typeName}
                            data={{ product }}
                            quickViewButton={quickviewSlot}
                            productComparisonButton={productComparisonButtonSlot}
                            isPriceMinMaxEnabled={isPriceMinMaxEnabled?.IsEnabled}
                            priceResources={priceResources}
                            dimensionAvailabilities={this.state.productsDimensionAvailabilities?.find(
                                dimensionAvailability =>
                                    ArrayExtensions.hasElements(dimensionAvailability) &&
                                    dimensionAvailability[0].masterProductId === (product.MasterProductId ?? product.RecordId)
                            )}
                            cartData={this.state.cartState}
                        />
                        <ProductCollectionQty
                            product={product}
                            context={this.props.context}
                            data={{ product: product1 }}
                            id={this.props.id}
                            cartData={this.state.cartState}
                            typeName='hello'
                            isBundleCollection={isBundleCollection}
                        />
                    </>
                )
            };
        }
        return null;
    };

    /**
     * Get the active price for products.
     */
    private readonly _getActivePrice = async (): Promise<void> => {
        const productsList = this.state.products;
        const products = await getPriceForProductSearchResult(
            new PriceForProductSearchResultInput(productsList),
            this.props.context.actionContext
        );
        if (Msdyn365.msdyn365Commerce.isBrowser) {
            await this._getProductsDimensionsAvailabilities(products);
        }

        this.setState({ products });
    };

    /**
     * Get the see all button.
     * @param recommendation - Recommendation.
     * @param headingLabel - Heading label.
     * @returns IProductComponentSeeAllViewProps - Product component see all view props.
     */
    private readonly _getSeeAllButton = (recommendation: string, headingLabel: string): React.ReactNode => {
        if (!Msdyn365.msdyn365Commerce.isBrowser) {
            return null;
        }

        const catalogId = getCatalogId(this.props.context.request);
        validateCatalogId(catalogId);
        const searchPageLink = getUrlSync('search', this.props.context.actionContext);
        if (!searchPageLink) {
            return null;
        }
        var categoryId = '';
        // Get category Id
        if (this.props.config.productCollection['categoryIds']) {
            categoryId = this.props.config.productCollection['categoryIds'][0];
        }
        const url = new URL(searchPageLink, window.location.href);
        const extend_href = `${url.origin}/${categoryId}` + '.c';
        url.searchParams.set('recommendation', recommendation);
        url.searchParams.set('title', headingLabel);
        url.searchParams.set('catalogid', `${catalogId}`);

        return (
            <Button
                className='ms-product-collection__see-all-btn msc-cta__primary'
                title={this.props.resources.seeAllButtonText}
                href={extend_href}
            >
                <div className='all-text'>View All</div>
                <div className='arrow-all'>
                    <svg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'>
                        <path
                            d='M3.90668 11C4.14543 11 4.37849 10.9053 4.54903 10.7382L8.73283 6.63884C8.90336 6.47175 9 6.24338 9 6.00945V5.9816C9 5.74767 8.90336 5.51931 8.73283 5.35221L4.54903 1.25283C4.19091 0.907506 3.61108 0.918646 3.26433 1.26954C2.92326 1.61487 2.92326 2.16628 3.26433 2.51161L6.81715 5.99831L3.26433 9.47944C2.91189 9.83034 2.91189 10.3929 3.26433 10.7382C3.43486 10.9053 3.66224 11 3.90668 11Z'
                            fill='#4D4851'
                        />
                    </svg>
                </div>
            </Button>
        );
    };
}

export default ProductCollection;
